import {Box, HBox} from '../../components/core/view';
import {Platform, StyleSheet} from 'react-native';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  useColorThemeType,
  useFontTheme,
  useShadow,
} from '../../appStyles/hooks/theme';
import {
  useGetOrderItemInfo,
  useItemReplacementReducer,
} from '../hooks/itemReplacement';

import B2BLocationInStore from '../components/ItemDetail/B2BLocationInStore';
import {BackHeaderLeft} from '@buncha/common/components/BackHeaderLeft';
import BuyerInfoCell from '../components/BuyerInfoCell';
import ChatBottomsheet from '../components/ItemDetail/contactBottomSheet/ChatBottomsheet';
import {ChatModes} from '@buncha/batchDetails/types/Chat';
import ChatOpener from '@buncha/components/composites/ChatOpener';
import DropItemModalBody from '../components/ItemDetail/DropItemModalBody/DropItemModalBody';
import {H6Text} from '../../../src/components/core/text';
import Header from '../../components/composites/Header/Header';
import If from '@buncha/components/conditional/If';
import {ItemDetailsScreen} from '../navigation/types';
import {ItemReplacementContext} from '../context/itemReplacement';
import ItemToBeReplacedCell from '../components/itemReplacement/itemToBeReplacedCell';
import LocationInStore from '../components/ItemDetail/locationInStore';
import {Modules} from '@buncha/utils/types';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {ReplacementItemDetails} from '../types/itemReplacement';
import ReplacementSuggestions from '../components/itemReplacement/replacementSuggestions';
import {SubstitutionChoice} from '../types/itemDetails';
import {Touchable} from '@buncha/components/core/touchable';
import {UserTypes} from '@buncha/onBoarding/types';
import {navigation} from '@buncha/config/navigation';
import {useAppSelector} from '../../config/redux/hooks';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsFocused} from '@react-navigation/native';
import {useIsMeijerStore} from '@buncha/home/hooks/home';

type Props = NativeStackScreenProps<any, any>;

const ItemReplacement: React.FC<Props> = props => {
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const [state, dispatch] = useItemReplacementReducer();
  const provider = useMemo(() => ({state, dispatch}), [dispatch, state]);
  const {body} =
    useGetLocale(Modules.itemDetails, ItemDetailsScreen.ItemReplacement) ?? {};

  const params = props?.route?.params;
  const runId = Number(params?.runId) || 0;
  const orderId = Number(params?.orderId) || 0;
  const orderItemId = Number(params?.orderItemId) || 0;
  const hasSubstitute = Boolean(params?.hasSubstitute) || 0;
  const substitutionChoice = params?.substitutionChoice;
  const [showContactSheet, setShowContactSheet] = useState(false);
  const isFocus = useIsFocused();
  const shadow = useShadow('2');

  const {getOrderItemInfo, loading, data} = useGetOrderItemInfo(dispatch);
  const {upcOrderItem} = useAppSelector(gState => gState.itemScanner);
  const storeId = data?.order?.run?.store?.id ?? 0;
  const buyer = data?.order?.buyer;
  const isMeijerStore = useIsMeijerStore(storeId);
  const flexStyling = isMeijerStore ? 1 : 0.85;

  const [showDropModal, setShowDropModal] = useState(false);

  const item = useMemo(() => {
    if (params?.isUPCMode)
      return {
        id: upcOrderItem?.id,
        itemImageUrl: upcOrderItem?.itemImageUrl,
        name: upcOrderItem?.name,
        quantity: upcOrderItem?.quantity,
        catalogItemId: upcOrderItem?.catalogItemId,
        tags: [],
        price: upcOrderItem?.price,
      };
    return data;
  }, [
    data,
    params?.isUPCMode,
    upcOrderItem?.catalogItemId,
    upcOrderItem?.id,
    upcOrderItem?.itemImageUrl,
    upcOrderItem?.name,
    upcOrderItem?.price,
    upcOrderItem?.quantity,
  ]);

  useEffect(() => {
    if (!params?.isUPCMode && runId) getOrderItemInfo(orderItemId, runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleContactSheet = useCallback(() => {
    setShowContactSheet(!showContactSheet);
  }, [showContactSheet]);

  const onCloseAOOSModal = useCallback(() => {
    navigation.goBack();
  }, []);

  const toggleModal = useCallback(() => {
    setShowDropModal(!showDropModal);
  }, [showDropModal]);
  const headerMenu = useMemo(() => {
    return (
      <HBox style={[styles.header, {backgroundColor: theme?.text.blue[300]}]}>
        <If condition={isMeijerStore && state?.item?.itemLocation}>
          <B2BLocationInStore
            storeId={storeId}
            location={state.item?.itemLocation}
          />
        </If>
        <If condition={!isMeijerStore}>
          <BackHeaderLeft
            containerStyle={{backgroundColor: theme?.text.blue[300]}}
            iconColor={theme?.text.white[100]}
          />
          <If condition={data?.tags}>
            <LocationInStore category={state.item?.tags} />
          </If>
        </If>
      </HBox>
    );
  }, [
    data?.tags,
    isMeijerStore,
    state.item?.itemLocation,
    state.item?.tags,
    storeId,
    theme?.text.blue,
    theme?.text.white,
  ]);

  const customerTypeString = useMemo(() => {
    if (buyer?.hasBunchaClub) return '👑 Buncha Club';
    else if (buyer?.order?.buyer?.isVip) return '👑 VIP Customer';
    else if (buyer?.hasNewCustomer) return 'New Customer';
    return '';
  }, [buyer?.hasBunchaClub, buyer?.hasNewCustomer, buyer?.order?.buyer?.isVip]);
  return (
    <Header
      loading={loading}
      style={[
        styles.parentBox,
        {backgroundColor: theme?.background?.gray?.[200]},
      ]}
      headerColor={theme?.background?.blue?.[300]}
      safeAreaViewColor={theme?.background?.gray?.[200]}
      showDefaultLeftHeader={false}
      headerRightComponent={headerMenu}>
      <ItemReplacementContext.Provider value={provider}>
        <Box style={styles.mainContainer}>
          <If
            condition={
              substitutionChoice &&
              (substitutionChoice === SubstitutionChoice.meijerBrand ||
                substitutionChoice === SubstitutionChoice.nationalBrand) &&
              !state?.item?.substitutionOptions?.length
            }>
            <Box>
              <H6Text
                style={[
                  styles.headerText,
                  styles.subsBox,
                  {
                    color: theme?.text?.blue?.[300],
                    fontFamily: muli?.[700]?.normal,
                    backgroundColor: theme?.background?.yellow?.[200],
                  },
                ]}>
                <If
                  condition={
                    substitutionChoice === SubstitutionChoice.meijerBrand
                  }>
                  {body?.selectReplacementB2B}
                </If>
                <If
                  condition={
                    substitutionChoice === SubstitutionChoice.nationalBrand
                  }>
                  {body?.selectReplacement}
                </If>
              </H6Text>
            </Box>
          </If>
          <If
            condition={
              (state.item?.substitutionChoice ===
                SubstitutionChoice.byShopper ||
                state.item?.substitutionChoice === SubstitutionChoice.none ||
                state.item?.substitutionChoice ===
                  SubstitutionChoice.meijerBrand ||
                state.item?.substitutionChoice ===
                  SubstitutionChoice.nationalBrand) &&
              !state.item?.substitutionOptions
            }>
            <HBox
              style={[
                styles.disclaimerBox,
                {
                  backgroundColor: theme?.background?.yellow?.[200],
                },
              ]}>
              <H6Text
                style={[
                  styles.headerText,
                  {
                    color: theme?.text?.blue?.[300],
                    fontFamily: muli?.[700]?.normal,
                    flex: flexStyling,
                  },
                ]}>
                {body?.getApproval}
              </H6Text>
              <If condition={!isMeijerStore}>
                <ChatOpener
                  hideRole={UserTypes.driver}
                  mode={ChatModes.run}
                  runId={isFocus ? runId : undefined}
                  width={300}
                  height={300}
                  style={styles.chatIcon}
                />
              </If>
            </HBox>
          </If>
          <Box
            style={[
              styles.bodyContainer,
              styles.subBodyContainer,
              {
                backgroundColor: theme?.background?.white?.[100],
              },
              shadow,
            ]}>
            <BuyerInfoCell
              customerType={customerTypeString}
              buyer={buyer}
              isNSC2Item={state?.item?.isNSC2Item}
              orderItemAttribute={state?.item?.orderItemAttribute}
              isVip={buyer?.isVip}
            />

            <ItemToBeReplacedCell
              itemData={item as ReplacementItemDetails}
              isUPCMode={params?.isUPCMode}
            />
          </Box>
          <ReplacementSuggestions
            catalogItemId={data?.catalogItemId || 0}
            category={data?.tags || ''}
            searchQuery={data?.name || ''}
            storeId={storeId}
            subCategory={''}
            isUPCMode={params?.isUPCMode}
            orderItemId={orderItemId}
            orderId={data?.order?.id}
            runId={data?.order?.run?.id}
            quantity={data?.quantity}
            isMeijerStore={isMeijerStore}
          />
        </Box>
        <Touchable
          onPress={toggleModal}
          style={[
            styles.dropTouchable,
            {backgroundColor: theme?.background.gray[200]},
          ]}>
          <H6Text style={{color: theme?.text.red[100]}}>
            If suggested backup isn't approved by customer, drop this item
          </H6Text>
        </Touchable>

        <If condition={showContactSheet && hasSubstitute}>
          <ChatBottomsheet
            isOpen={showContactSheet}
            onClose={toggleContactSheet}
            runId={runId}
            orderId={orderId}
            storeId={storeId}
          />
        </If>
        <DropItemModalBody
          open={showDropModal}
          orderItemId={orderItemId}
          onClose={toggleModal}
          isUPCMode={false}
          catalogItemId={state.item?.catalogItemId}
          orderId={orderId}
          onCloseAOOSModal={onCloseAOOSModal}
        />
      </ItemReplacementContext.Provider>
    </Header>
  );
};

export default ItemReplacement;

const styles = StyleSheet.create({
  parentBox: {
    flex: 1,
    position: 'relative',
    justifyContent: 'center',
  },
  header: {
    flex: 1,
  },
  headerText: {
    textAlign: 'left',
  },
  loader: {
    alignSelf: 'center',
  },
  mainContainer: {
    flex: 1,
  },
  actionButton: {
    borderRadius: 50,
    marginTop: 10,
  },
  chatIcon: {
    width: 40,
    height: 40,
    flex: 0.1,
    marginTop: 0,
    marginLeft: 0,
  },
  disclaimerBox: {
    marginTop: 2,
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    paddingVertical: 5,
    alignItems: 'center',
  },
  disclaimertext: {
    flex: 0.85,
  },
  subsBox: {
    padding: 5,
  },
  dropTouchable: {
    marginHorizontal: 20,
    marginBottom: Platform.select({android: 20}),
  },
  bodyContainer: {
    paddingVertical: 15,
    paddingHorizontal: 10,
    marginHorizontal: 15,
    borderRadius: 14,
    marginBottom: 10,
  },
  subBodyContainer: {
    marginTop: 10,
  },
});
